import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  Alert,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import DeleteIcon from '@mui/icons-material/Delete';
import { useDeploy } from '../contexts/DeployContext';

function formatDate(s) {
  if (!s) return '—';
  try {
    const d = new Date(s);
    const y = d.getFullYear();
    const mo = String(d.getMonth() + 1).padStart(2, '0');
    const da = String(d.getDate()).padStart(2, '0');
    const h = String(d.getHours()).padStart(2, '0');
    const mi = String(d.getMinutes()).padStart(2, '0');
    return `${y}-${mo}-${da} ${h}:${mi}`;
  } catch {
    return s;
  }
}

function describeSpec(spec) {
  if (!spec || typeof spec !== 'object') return '—';
  const parts = [];
  const tuneCount = Object.keys(spec.tune || {}).length;
  const disableCount = (spec.disable || []).length;
  const customCount = Object.keys(spec.custom_signals || {}).length;
  if (tuneCount) parts.push(`tune:${tuneCount}`);
  if (disableCount) parts.push(`disable:${disableCount}`);
  if (customCount) parts.push(`custom:${customCount}`);
  return parts.length ? parts.join(' · ') : 'defaults';
}

function IngestsTable({ selected, onSelect, refreshKey = 0, onChanged }) {
  const { deployApi } = useDeploy();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await deployApi.ingests.list();
      setRows(res?.overlays ?? []);
    } catch (err) {
      setError(err?.message ?? 'Failed to load ingests');
    } finally {
      setLoading(false);
    }
  }, [deployApi]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows, refreshKey]);

  const handleActivate = useCallback(async (id) => {
    setBusyId(id);
    try {
      await deployApi.ingests.activate(id);
      onChanged?.();
    } catch (err) {
      setError(err?.message ?? 'Activate failed');
    } finally {
      setBusyId(null);
    }
  }, [deployApi, onChanged]);

  const handlePause = useCallback(async (id) => {
    setBusyId(id);
    try {
      await deployApi.ingests.pause(id);
      onChanged?.();
    } catch (err) {
      setError(err?.message ?? 'Pause failed');
    } finally {
      setBusyId(null);
    }
  }, [deployApi, onChanged]);

  const handleDelete = useCallback(async (id) => {
    if (!window.confirm('Delete this ingest?')) return;
    setBusyId(id);
    try {
      await deployApi.ingests.delete(id);
      onChanged?.();
    } catch (err) {
      setError(err?.message ?? 'Delete failed');
    } finally {
      setBusyId(null);
    }
  }, [deployApi, onChanged]);

  return (
    <Paper variant="outlined" sx={{ p: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
        <Typography variant="subtitle1" sx={{ flex: 1, fontWeight: 600 }}>Ingests</Typography>
        {loading && <CircularProgress size={18} />}
      </Box>
      {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 1 }}>{error}</Alert>}
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Preset</TableCell>
              <TableCell>Overlay</TableCell>
              <TableCell>Write</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Enabled</TableCell>
              <TableCell>Updated</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={8}>
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                    No ingests yet. Build one on the right with the DSL: <code>mbd.ingest('name').preset('hyperliquid').tune(...).execute()</code>.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {rows.map((row) => {
              const isSelected = selected?.overlay_id === row.overlay_id;
              const isActive = row.status === 'active';
              return (
                <TableRow
                  key={row.overlay_id}
                  hover
                  selected={isSelected}
                  onClick={() => onSelect?.(row)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell><strong>{row.name}</strong></TableCell>
                  <TableCell><code>{row.preset}</code></TableCell>
                  <TableCell><Typography variant="caption">{describeSpec(row.spec)}</Typography></TableCell>
                  <TableCell><Chip size="small" label={row.spec?.write_to || 'alpha'} variant="outlined" /></TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={row.status}
                      color={isActive ? 'success' : 'default'}
                      variant={isActive ? 'filled' : 'outlined'}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={row.enabled ? 'on' : 'off'}
                      color={row.enabled ? 'primary' : 'default'}
                      variant={row.enabled ? 'filled' : 'outlined'}
                    />
                  </TableCell>
                  <TableCell><Typography variant="caption">{formatDate(row.updated_at)}</Typography></TableCell>
                  <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                    {isActive ? (
                      <Tooltip title="Pause">
                        <IconButton size="small" disabled={busyId === row.overlay_id} onClick={() => handlePause(row.overlay_id)}>
                          <PauseIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Tooltip title="Activate">
                        <IconButton size="small" disabled={busyId === row.overlay_id} onClick={() => handleActivate(row.overlay_id)}>
                          <PlayArrowIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Delete">
                      <IconButton size="small" disabled={busyId === row.overlay_id} onClick={() => handleDelete(row.overlay_id)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      {rows.some((r) => !r.enabled) && (
        <Alert severity="info" sx={{ mt: 1.5 }}>
          New overlays default to <strong>enabled=off</strong>. An admin must flip the flag on the row before alpha applies it. Until then, the overlay sits unused.
        </Alert>
      )}
    </Paper>
  );
}

export default IngestsTable;
